/* istanbul ignore file */

import type { Client } from 'urql';

import type { LastPlacedOrderDetailsQuery } from '../../graphql/LastPlacedOrders.generated';
import { LastPlacedOrderDetailsDocument } from '../../graphql/LastPlacedOrders.generated';
import type {
  LocationsSearchByAreaQuery,
  LocationsSearchByAreaQueryVariables,
  LocationsSearchByAreaWithDisclosureFieldsQuery,
} from '../../graphql/LocationsSearchByArea.generated';
import {
  LocationsSearchByAreaDocument,
  LocationsSearchByAreaWithDisclosureFieldsDocument,
} from '../../graphql/LocationsSearchByArea.generated';
import {
  LocationsSearchByGeoDocument,
  type LocationsSearchByGeoQuery,
  type LocationsSearchByGeoQueryVariables,
} from '../../graphql/LocationsSearchByGeo.generated';
import {
  LocationsSearchByRegionDocument,
  type LocationsSearchByRegionQuery,
  type LocationsSearchByRegionQueryVariables,
} from '../../graphql/LocationsSearchByRegion.generated';
import {
  LocationsSearchByRestaurantNameDocument,
  type LocationsSearchByRestaurantNameQuery,
  type LocationsSearchByRestaurantNameQueryVariables,
} from '../../graphql/LocationsSearchByRestaurantName.generated';
import {
  LocationsSearchBySearchStringDocument,
  type LocationsSearchBySearchStringQuery,
  type LocationsSearchBySearchStringQueryVariables,
  LocationsSearchBySearchStringWithDisclosureFieldsDocument,
  type LocationsSearchBySearchStringWithDisclosureFieldsQuery,
} from '../../graphql/LocationsSearchBySearchString.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Queries locations list based on provided search string
 */
export async function locationsSearchByStringQuery(
  client: Client,
  variables: LocationsSearchBySearchStringQueryVariables,
) {
  const { searchString } = variables;
  const queryLocations = client.query<LocationsSearchBySearchStringQuery>;

  return queryLocations(
    LocationsSearchBySearchStringDocument,
    { searchString },
    { requestPolicy: 'cache-and-network' },
  ).toPromise();
}

/**
 * Queries locations list based on provided search string
 */
export async function locationsSearchByRestaurantNameQuery(
  client: Client,
  variables: LocationsSearchByRestaurantNameQueryVariables,
) {
  const { query } = variables;
  const queryLocations = client.query<LocationsSearchByRestaurantNameQuery>;

  return queryLocations(
    LocationsSearchByRestaurantNameDocument,
    { query },
    { requestPolicy: 'network-only' },
  ).toPromise();
}

export async function locationsSearchByGeoQuery(
  client: Client,
  variables: LocationsSearchByGeoQueryVariables,
) {
  const { latitude, longitude, radius } = variables;
  const queryLocations = client.query<LocationsSearchByGeoQuery>;

  return queryLocations(
    LocationsSearchByGeoDocument,
    { latitude, longitude, radius },
    { requestPolicy: 'network-only' },
  ).toPromise();
}

export async function locationsSearchByRegion(
  client: Client,
  variables: LocationsSearchByRegionQueryVariables,
) {
  const queryLocations = client.query<LocationsSearchByRegionQuery>;

  return queryLocations(LocationsSearchByRegionDocument, variables, {
    requestPolicy: 'network-only',
  }).toPromise();
}

/**
 * Queries locations list (+ extra disclosure fields) based on provided search string
 */
export async function locationsSearchByStringWithDisclosureFieldsQuery(
  client: Client,
  variables: LocationsSearchBySearchStringQueryVariables,
) {
  const { searchString } = variables;
  const queryLocations =
    client.query<LocationsSearchBySearchStringWithDisclosureFieldsQuery>;

  return queryLocations(
    LocationsSearchBySearchStringWithDisclosureFieldsDocument,
    { searchString },
    { requestPolicy: 'cache-and-network' },
  ).toPromise();
}

/**
 * Queries locations based on provided search area
 */
export async function locationsSearchByAreaQuery(
  client: Client,
  variables: LocationsSearchByAreaQueryVariables,
) {
  const queryLocations = client.query<LocationsSearchByAreaQuery>;

  return queryLocations(LocationsSearchByAreaDocument, variables, {
    requestPolicy: 'cache-and-network',
  }).toPromise();
}

/**
 * Queries locations (+ extra disclosure fields) based on provided search area
 */
export async function locationsSearchByAreaWithDisclosureFieldsQuery(
  client: Client,
  variables: LocationsSearchByAreaQueryVariables,
) {
  const queryLocations =
    client.query<LocationsSearchByAreaWithDisclosureFieldsQuery>;

  return queryLocations(
    LocationsSearchByAreaWithDisclosureFieldsDocument,
    variables,
    {
      requestPolicy: 'cache-and-network',
    },
  ).toPromise();
}

/**
 * Queries last placed orders details
 */
export async function lastPlacedOrdersDetailsQuery(client: Client) {
  return client
    .query<LastPlacedOrderDetailsQuery>(
      LastPlacedOrderDetailsDocument,
      { input: { page: 1, take: 10 } },
      { requestPolicy: 'cache-first' },
    )
    .toPromise();
}
